import { supabase } from '../lib/supabase'
import type {
  CreateIaDocumentoChatPayload,
  IaDocumentoChat,
  IaDocumentoMessage,
  IaDocumentoMessageResult,
  IaDocumentoUploadResult,
  InsertIaDocumentoMessagePayload,
} from '../types/iaDocumento'

export const IA_DOCUMENTO_WEBHOOK_URL =
  'https://webhook.pasiai.com/webhook/d30bc17a-e802-4d18-b093-91117a5a7b20'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function pickString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return undefined
}

function pickUuid(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && UUID_RE.test(value.trim())) {
      return value.trim()
    }
  }
  return undefined
}

function pickTessRootId(...values: unknown[]): number | string | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim()) {
      const n = Number(value.trim())
      if (Number.isFinite(n)) return n
      return value.trim()
    }
  }
  return null
}

/** Procura texto de resposta da Tess em vários formatos do n8n. */
function extractAiReply(data: unknown, depth = 0): string | null {
  if (data == null || depth > 6) return null

  if (typeof data === 'string' && data.trim()) return data.trim()

  if (Array.isArray(data)) {
    for (const item of data) {
      const found = extractAiReply(item, depth + 1)
      if (found) return found
    }
    return null
  }

  if (typeof data !== 'object') return null
  const obj = data as Record<string, unknown>

  // Formato Tess: { responses: [ { output, input, root_id, ... } ] }
  if (Array.isArray(obj.responses) && obj.responses.length > 0) {
    const fromResponses = extractAiReply(obj.responses, depth + 1)
    if (fromResponses) return fromResponses
  }

  // Preferência: campos típicos de resposta da IA (não usar "input" / "message" cedo)
  const preferred = pickString(
    obj.output,
    obj.reply,
    obj.ai_message,
    obj.answer,
    obj.response,
    obj.content,
    obj.text,
    obj.result
  )
  if (preferred) return preferred

  for (const key of ['json', 'data', 'body', 'choices', 'message', 'answers']) {
    if (obj[key] == null) continue
    if (key === 'message' && typeof obj[key] === 'object') {
      const nestedMsg = obj[key] as Record<string, unknown>
      const fromRole = pickString(nestedMsg.content, nestedMsg.text, nestedMsg.output)
      if (fromRole) return fromRole
    }
    const nested = extractAiReply(obj[key], depth + 1)
    if (nested) return nested
  }

  return null
}

/** Extrai o primeiro objeto útil da resposta do n8n (array, nested data/body/json). */
function unwrapPayload(data: unknown, depth = 0): Record<string, unknown> {
  if (!data || typeof data !== 'object' || depth > 4) return {}

  if (Array.isArray(data)) {
    for (const item of data) {
      const unwrapped = unwrapPayload(item, depth + 1)
      if (Object.keys(unwrapped).length > 0) return unwrapped
    }
    return {}
  }

  const obj = data as Record<string, unknown>

  if (
    pickUuid(obj.chat_id, obj.id) ||
    obj.tess_root_id != null ||
    obj.root_id != null ||
    obj.document_id != null
  ) {
    return obj
  }

  for (const key of ['json', 'data', 'body', 'record', 'rows']) {
    if (obj[key] != null) {
      const nested = unwrapPayload(obj[key], depth + 1)
      if (Object.keys(nested).length > 0) return nested
    }
  }

  return obj
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const iaDocumentoService = {
  async listChats(profileId: string): Promise<IaDocumentoChat[]> {
    const { data, error } = await supabase
      .from('ai_document_chats')
      .select('*')
      .eq('profile_id', profileId)
      .eq('deleted', false)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data as IaDocumentoChat[]) ?? []
  },

  async getChatById(
    chatId: string,
    profileId: string
  ): Promise<IaDocumentoChat | null> {
    const { data, error } = await supabase
      .from('ai_document_chats')
      .select('*')
      .eq('id', chatId)
      .eq('profile_id', profileId)
      .eq('deleted', false)
      .maybeSingle()

    if (error) throw error
    return (data as IaDocumentoChat) ?? null
  },

  async listMessages(chatId: string): Promise<IaDocumentoMessage[]> {
    const { data, error } = await supabase
      .from('ai_document_messages')
      .select('*')
      .eq('chat_id', chatId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return (data as IaDocumentoMessage[]) ?? []
  },

  async createChat(payload: CreateIaDocumentoChatPayload): Promise<IaDocumentoChat> {
    const { data, error } = await supabase
      .from('ai_document_chats')
      .insert({
        profile_id: payload.profile_id,
        subject: payload.subject?.trim() || payload.file_name || 'Novo chat',
        document_id: payload.document_id ?? null,
        file_name: payload.file_name ?? null,
        deleted: false,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as IaDocumentoChat
  },

  async renameChat(chatId: string, subject: string): Promise<void> {
    const { error } = await supabase
      .from('ai_document_chats')
      .update({ subject: subject.trim() })
      .eq('id', chatId)

    if (error) throw error
  },

  async softDeleteChat(chatId: string): Promise<void> {
    const { error } = await supabase
      .from('ai_document_chats')
      .update({ deleted: true })
      .eq('id', chatId)

    if (error) throw error
  },

  async insertMessage(payload: InsertIaDocumentoMessagePayload): Promise<IaDocumentoMessage> {
    const { data, error } = await supabase
      .from('ai_document_messages')
      .insert({
        chat_id: payload.chat_id,
        user_message: payload.user_message,
        ai_message: payload.ai_message,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as IaDocumentoMessage
  },

  /**
   * Completa metadados do chat criado pelo n8n (subject/arquivo) sem criar outro registro.
   */
  async enrichChatFromUpload(
    chat: IaDocumentoChat,
    file: File,
    documentId: string | null
  ): Promise<IaDocumentoChat> {
    const nextSubject =
      chat.subject && chat.subject !== 'Novo chat' ? chat.subject : file.name
    const nextFileName = chat.file_name || file.name
    const nextDocumentId = chat.document_id || documentId || null

    const needsUpdate =
      nextSubject !== chat.subject ||
      nextFileName !== chat.file_name ||
      nextDocumentId !== chat.document_id

    if (!needsUpdate) return chat

    const { data, error } = await supabase
      .from('ai_document_chats')
      .update({
        subject: nextSubject,
        file_name: nextFileName,
        document_id: nextDocumentId,
      })
      .eq('id', chat.id)
      .select('*')
      .single()

    if (!error && data) return data as IaDocumentoChat
    return {
      ...chat,
      subject: nextSubject,
      file_name: nextFileName,
      document_id: nextDocumentId,
    }
  },

  /**
   * Após o webhook OK, tenta achar o chat que o n8n gravou (PasiGPT-style).
   */
  async resolveChatCreatedByN8n(params: {
    profileId: string
    remoteChatId?: string
    tessRootId?: number | string | null
    uploadedAfterIso: string
  }): Promise<IaDocumentoChat | null> {
    const attempts = 5
    const delayMs = 800

    for (let i = 0; i < attempts; i++) {
      if (i > 0) await sleep(delayMs)

      if (params.remoteChatId) {
        const byId = await this.getChatById(params.remoteChatId, params.profileId)
        if (byId) return byId
      }

      const chats = await this.listChats(params.profileId)
      const recent = chats.filter(
        (c) => new Date(c.created_at).getTime() >= new Date(params.uploadedAfterIso).getTime() - 5000
      )

      if (params.tessRootId != null && params.tessRootId !== '') {
        const byTess = recent.find(
          (c) =>
            c.tess_root_id != null &&
            String(c.tess_root_id) === String(params.tessRootId)
        )
        if (byTess) return byTess
      }

      const withTess = recent.find((c) => c.tess_root_id != null && c.tess_root_id !== '')
      if (withTess) return withTess

      // n8n às vezes cria "Novo chat" antes de gravar tess_root_id
      if (params.remoteChatId) {
        const match = recent.find((c) => c.id === params.remoteChatId)
        if (match) return match
      }
    }

    return null
  },

  /**
   * Envia documento ao n8n/Tess.
   * Preferência: n8n cria o chat no Supabase e devolve chat_id (ou id do insert).
   * Fallback local só se o webhook falhar ou o chat não aparecer no banco.
   */
  async uploadDocumento(params: {
    file: File
    profileId: string
  }): Promise<{
    chat: IaDocumentoChat
    documentId: string | null
    raw: IaDocumentoUploadResult
    webhookOk: boolean
  }> {
    let raw: IaDocumentoUploadResult = {}
    let documentId: string | null = null
    let remoteChatId: string | undefined
    let tessRootId: number | string | null = null
    let webhookOk = false
    const uploadedAfterIso = new Date().toISOString()

    try {
      const formData = new FormData()
      formData.append('arquivo', params.file)
      formData.append('profile_id', params.profileId)
      formData.append('action', 'upload')

      const response = await fetch(IA_DOCUMENTO_WEBHOOK_URL, {
        method: 'POST',
        body: formData,
      })

      if (response.ok) {
        webhookOk = true
        try {
          const json = await response.json()
          raw = unwrapPayload(json) as IaDocumentoUploadResult
        } catch {
          raw = {}
        }

        // chat_id explícito OU id do insert Supabase (uuid) — NÃO usar id numérico da Tess
        remoteChatId = pickUuid(raw.chat_id, raw.id)
        documentId = pickString(raw.document_id, raw.session_id) ?? null
        tessRootId = pickTessRootId(raw.tess_root_id, raw.root_id)
      }
    } catch {
      webhookOk = false
    }

    let chat: IaDocumentoChat | null = null

    if (webhookOk) {
      chat = await this.resolveChatCreatedByN8n({
        profileId: params.profileId,
        remoteChatId,
        tessRootId,
        uploadedAfterIso,
      })
    }

    // Fallback: só cria chat local se o n8n não criou / webhook falhou
    if (!chat) {
      chat = await this.createChat({
        profile_id: params.profileId,
        subject: params.file.name,
        document_id: documentId,
        file_name: params.file.name,
      })
    } else {
      chat = await this.enrichChatFromUpload(chat, params.file, documentId)
    }

    return {
      chat,
      documentId: chat.document_id ?? documentId,
      raw: {
        ...raw,
        chat_id: chat.id,
        document_id: chat.document_id ?? documentId ?? undefined,
        tess_root_id: chat.tess_root_id ?? tessRootId ?? undefined,
      },
      webhookOk,
    }
  },

  /**
   * Envia pergunta ao n8n/Tess.
   * Preferência: n8n grava o par user/ai → UI rele.
   * Se o webhook OK mas ainda não gravou no banco, usa o texto da resposta HTTP.
   * Só mostra “Tess indisponível” quando o webhook falhar de verdade.
   */
  async enviarPergunta(params: {
    profileId: string
    chatId: string
    documentId: string | null
    message: string
    tessRootId?: number | string | null
  }): Promise<{ messages: IaDocumentoMessage[]; reply: string | null; webhookOk: boolean }> {
    const previous = await this.listMessages(params.chatId)
    const previousCount = previous.length

    let raw: IaDocumentoMessageResult = {}
    let reply: string | null = null
    let webhookOk = false
    let rawJson: unknown = null

    try {
      const response = await fetch(IA_DOCUMENTO_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'message',
          profile_id: params.profileId,
          chat_id: params.chatId,
          document_id: params.documentId,
          tess_root_id: params.tessRootId ?? null,
          message: params.message,
        }),
      })

      if (response.ok) {
        webhookOk = true
        try {
          rawJson = await response.json()
          raw = unwrapPayload(rawJson) as IaDocumentoMessageResult
        } catch {
          raw = {}
          rawJson = null
        }
        reply =
          extractAiReply(rawJson) ??
          pickString(
            raw.output,
            raw.reply,
            raw.ai_message,
            raw.answer,
            raw.response,
            raw.content,
            raw.text
          ) ??
          null

        // Aguarda o n8n gravar em ai_document_messages (estilo PasiGPT)
        for (let i = 0; i < 4; i++) {
          await sleep(i === 0 ? 1200 : 800)
          const current = await this.listMessages(params.chatId)
          if (current.length > previousCount) {
            return { messages: current, reply, webhookOk }
          }
        }
      }
    } catch {
      webhookOk = false
    }

    let messages = await this.listMessages(params.chatId)

    if (messages.length <= previousCount) {
      let aiText: string | null = null

      if (webhookOk && reply) {
        aiText = reply
      } else if (!webhookOk) {
        aiText =
          'A Tess ainda não está conectada (webhook n8n indisponível). Quando o fluxo estiver ativo, as respostas sobre o documento aparecerão aqui.'
      } else {
        // Webhook OK, Tess respondeu no n8n, mas o Respond não trouxe texto parseável
        // e ainda não gravou em ai_document_messages.
        aiText =
          'A Tess processou a mensagem, mas a resposta não voltou no formato esperado. Verifique o nó “Respond to Webhook” no n8n (campos output/reply/ai_message) ou se as mensagens estão sendo gravadas em ai_document_messages.'
      }

      await this.insertMessage({
        chat_id: params.chatId,
        user_message: params.message,
        ai_message: aiText,
      })
      messages = await this.listMessages(params.chatId)
      reply = aiText
    }

    return { messages, reply, webhookOk }
  },
}
